import { z } from 'zod';
import { ClaimReasonEnum, ClaimResolutionMethodEnum, ClaimStatusEnum, ShippingClaimLineItemTag, } from '../../../enums/gsp.js';
import { frontClaimConversation } from '../../../front.js';
import { enumToZodLiteralUnion, helperSchema } from '../../../zodExtensions.js';
import { claimAddress, claimAddressCreate } from '../address.js';
import { shippingClaimResolutionSummary } from '../claim.js';
import { fingerprintCreate } from '../claimRiskAssessment.js';
import { sharedClaimSourceUnion, shippingClaimSourceMerchantApp, } from '../claimSource.js';
import { timeline } from '../timeline.js';
const storeOrderClaim = z.object({
    id: z.number(),
    idFromPlatform: z.string(),
    currencyCode: z.string(),
    createdOn: z.string(),
    customerCurrencyConversionRate: z.number().optional(),
    platformAdminUrl: z.string().optional(),
    orderNo: z.string(),
    shippingTitle: helperSchema.optionalizedString,
    shippingAddress: claimAddress.optional(),
    orderTotal: z.number().optional(),
    shippingPrice: z.number().optional(),
    wasShippingProtected: z.boolean(),
    tags: z.array(z.string()).optional(),
    isGlobalE: z.boolean(),
});
// the resolution method is set on the claim itself before finalizing
export const shippingClaimFinalize = z.object({
    finalizationSource: shippingClaimSourceMerchantApp,
    useGiftCard: z.boolean(),
    reorderShippingTitle: z.string().optional(),
});
export const shippingClaimMessages = frontClaimConversation.nullable();
// added in one request so a single claim update covers the whole selection
export const shippingClaimLineItemAdd = z.array(z.object({
    idFromPlatform: z.string(),
    quantity: z.coerce.number().int().positive(),
}));
// a claim shows two kinds of line item: what the customer claimed off the original
// order, and what a merchant added as a replacement (which has no order line to point at)
const shippingClaimLineItemDisplay = z.object({
    quantity: z.number(),
    unitPrice: z.number().nullable(),
    sku: z.string().nullable(),
    name: z.string().nullable(),
    imgUrl: z.string().nullish(),
    optionsFromPlatform: z.array(z.object({ name: z.string(), value: z.string() })),
});
const shippingClaimLineItem = z.discriminatedUnion('kind', [
    shippingClaimLineItemDisplay.extend({
        kind: z.literal('line_item'),
        id: z.number(),
        storeOrderLineItemId: z.number(),
        tags: z.array(enumToZodLiteralUnion(ShippingClaimLineItemTag)),
    }),
    shippingClaimLineItemDisplay.extend({
        kind: z.literal('replacement_item'),
        id: z.string(),
    }),
]);
export const shippingClaimUpdate = z.object({
    status: enumToZodLiteralUnion(ClaimStatusEnum),
    resolutionMethod: enumToZodLiteralUnion(ClaimResolutionMethodEnum),
});
// replaces the claim's address outright, so it carries a whole one — a partial payload
// would store an address the claim read can no longer parse
export const shippingClaimUpdateCustomerInfo = claimAddressCreate;
const shippingClaimInventoryLevelDisplay = z.object({
    quantityNeeded: z.number(),
    inventoryExists: z.boolean(),
    locations: z.array(z.object({
        quantity: z.number(),
        isAvailable: z.boolean(),
        locationName: z.string(),
    })),
});
// discriminated the same way as `shippingClaimLineItem` so a level can be matched back to its item
const shippingClaimInventoryLevel = z.discriminatedUnion('kind', [
    shippingClaimInventoryLevelDisplay.extend({
        kind: z.literal('line_item'),
        id: z.number(),
    }),
    shippingClaimInventoryLevelDisplay.extend({
        kind: z.literal('replacement_item'),
        id: z.string(),
    }),
]);
export const shippingClaimInventoryLevels = z.array(shippingClaimInventoryLevel);
export const shippingClaimMerchant = z.object({
    id: z.number(),
    createdOn: z.string(),
    storeContacts: z.array(z.object({
        email: z.string(),
        name: z.string(),
    })),
    hasBeenInvoiced: z.boolean(),
    financialSummary: z.object({
        // ? the total value of the claim as we see it, eg the cost of the line items.
        totalValue: z.number(),
        // ? the up to date estimate of what Shopify indicates can be refunded against the order, (useful to see / understand)
        // omitted when the platform could not be reached, so it reads as unknown rather than zero
        refundableAmount: z.number().optional(),
        // order contains chargeback
        hasChargeback: z.boolean(),
    }),
    noteFromCustomer: z.string().optional(),
    noteFromMerchant: z.string().optional(),
    linkToCustomerUi: z.string().optional(),
    resolutionSummary: shippingClaimResolutionSummary.optional(),
    resolvedOn: z.string().nullable(),
    customerId: z.number().optional(),
    resolvedIn: sharedClaimSourceUnion.optional(),
    source: sharedClaimSourceUnion.optional(),
    reason: enumToZodLiteralUnion(ClaimReasonEnum),
    status: enumToZodLiteralUnion(ClaimStatusEnum),
    resolutionMethod: enumToZodLiteralUnion(ClaimResolutionMethodEnum),
    shippingAddress: claimAddress.optional(),
    images: z.array(z.string()).optional(),
    // ? in the shipping claim sense this represents the original order and the claimed lineItems
    originalStoreOrder: storeOrderClaim,
    customerName: z.string().nullable(),
    customerEmail: z.string().nullable(),
    shippingClaimLineItems: z.array(shippingClaimLineItem),
    // lines taken off the claim since it was filed; display only, never totalled
    removedLineItems: z.array(shippingClaimLineItemDisplay.extend({ id: z.number() })),
    timeline: timeline.optional(),
});
// todo: merge these types a little better
// shippingClaim used by customer UI (much less complicated than merchant UI)
export const shippingClaimCustomerUi = z.object({
    id: z.number(),
    createdOn: z.string(),
    noteFromCustomer: helperSchema.optionalizedString,
    reason: enumToZodLiteralUnion(ClaimReasonEnum),
    status: enumToZodLiteralUnion(ClaimStatusEnum),
    shippingAddress: claimAddress.optional(),
    originalStoreOrder: z.object({
        id: z.coerce.number(),
        lineItems: z.array(z.object({
            id: z.coerce.number(),
            quantity: z.coerce.number(),
        })),
    }),
});
export const shippingClaimCreateCustomer = shippingClaimCustomerUi
    .pick({
    reason: true,
    noteFromCustomer: true,
    originalStoreOrder: true,
    shippingAddress: true,
})
    .merge(z.object({
    images: z.array(z.string()).optional(),
    fingerprint: fingerprintCreate.optional(),
}));
export const shippingClaimCreateMerchant = shippingClaimCustomerUi
    .pick({
    reason: true,
    noteFromCustomer: true,
    shippingAddress: true,
    originalStoreOrder: true,
})
    .merge(z.object({
    resolutionMethod: enumToZodLiteralUnion(ClaimResolutionMethodEnum),
    notifyCustomer: z.boolean().optional(),
    noteFromMerchant: helperSchema.optionalizedString,
    source: shippingClaimSourceMerchantApp.optional(),
}));
export const shippingClaimListItem = shippingClaimMerchant
    .pick({
    id: true,
    status: true,
    reason: true,
    resolutionMethod: true,
    createdOn: true,
    customerName: true,
    customerEmail: true,
})
    .extend({
    storeId: z.number(),
    storeName: z.string(),
    tags: z.string().array(),
});
export const shippingClaimList = z.array(shippingClaimListItem);
//# sourceMappingURL=shippingClaim.js.map